/**
 * Report Faulty Streetlight — R6.10 worked-stub.
 * Two identification paths: tap-on-map OR pole-number entry.
 * Design improvement over Comms PDF spec (text-only) per Design Optimization §3.
 * Atlas: Report > Streetlight.
 */
"use client";

import { useState } from "react";
import Link from "next/link";
import { Lightbulb, MapPin, CheckCircle2, Upload } from "lucide-react";
import { useActivePersona } from "@/lib/persona";
import { useDemoSessionStore } from "@petci/shared-ui";
import { useT } from "@/lib/locale";

export default function StreetlightPage() {
  const t = useT();
  const persona = useActivePersona();
  const addRequest = useDemoSessionStore((s) => s.addRequest);

  const [method, setMethod] = useState<"map" | "pole">("map");
  const [poleNumber, setPoleNumber] = useState("");
  const [pinDropped, setPinDropped] = useState(false);
  const [description, setDescription] = useState("");
  const [photoUploaded, setPhotoUploaded] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [refNumber] = useState(() => {
    const num = Math.floor(10000 + Math.random() * 90000);
    return `PEL-SL-${num}`;
  });

  function handleSubmit() {
    addRequest(persona.id, {
      id: refNumber,
      type: "Streetlight Report",
      status: { state: "received", receivedAt: "2026-05-10" },
      summary: method === "pole"
        ? `Pole #${poleNumber} — ${description || "Faulty streetlight"}`
        : `Map pin near ${persona.serviceAddress.line1} — ${description || "Faulty streetlight"}`,
    });
    setSubmitted(true);
  }

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto space-y-4">
        <div className="card-surface text-center space-y-4 py-6">
          <div className="w-16 h-16 rounded-full bg-semantic-success/10 flex items-center justify-center mx-auto">
            <CheckCircle2 size={32} className="text-semantic-success-text" aria-hidden />
          </div>
          <h1 className="text-xl font-display font-bold text-neutral-900">
            {t("report.slSubmittedTitle")}
          </h1>
          <div className="rounded-lg bg-neutral-50 p-4">
            <p className="text-xs text-neutral-500 uppercase tracking-wider font-semibold">{t("report.reference")}</p>
            <p className="text-lg font-mono font-bold text-brand-primary-text">{refNumber}</p>
          </div>
          <Link href="/services/track" className="btn-primary w-full justify-center">
            {t("report.trackRequests")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <Link
        href="/report"
        className="inline-flex items-center gap-1 text-sm text-brand-primary-text hover:underline py-1 -my-1"
      >
        ← {t("report.title")}
      </Link>

      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-lg bg-brand-primary/10 flex items-center justify-center">
          <Lightbulb size={20} className="text-brand-primary-text" aria-hidden />
        </span>
        <div>
          <h1 className="text-xl font-display font-bold text-neutral-900">
            {t("report.slTitle")}
          </h1>
          <p className="text-xs text-neutral-500">
            {t("report.slSubtitle")}
          </p>
        </div>
      </div>

      {/* Identification method tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setMethod("map")}
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors duration-fast ${
            method === "map"
              ? "bg-brand-primary-hover text-white"
              : "bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
          }`}
        >
          <MapPin size={14} className="inline mr-1" aria-hidden />
          {t("report.tapOnMap")}
        </button>
        <button
          onClick={() => setMethod("pole")}
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors duration-fast ${
            method === "pole"
              ? "bg-brand-primary-hover text-white"
              : "bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
          }`}
        >
          {t("report.enterPoleNumber")}
        </button>
      </div>

      <div className="card-surface space-y-4">
        {method === "map" ? (
          <div>
            <p className="text-sm font-medium text-neutral-700 mb-2">
              {t("report.tapFaultyLight")}
            </p>
            {/* Map placeholder — in production this would be MapLibre GL */}
            <div
              className={`relative rounded-lg overflow-hidden border-2 transition-colors duration-fast cursor-pointer ${
                pinDropped ? "border-semantic-success" : "border-neutral-200 hover:border-brand-primary"
              }`}
              style={{ height: 240, background: "linear-gradient(135deg, #F5F0EB 0%, #E8E3DC 50%, #F0EDE8 100%)" }}
              onClick={() => setPinDropped(true)}
            >
              {/* Simulated map with roads */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 240" aria-hidden>
                <line x1="0" y1="120" x2="400" y2="120" stroke="#A49E94" strokeWidth="2" strokeDasharray="8,4" />
                <line x1="200" y1="0" x2="200" y2="240" stroke="#A49E94" strokeWidth="2" strokeDasharray="8,4" />
                <line x1="80" y1="0" x2="320" y2="240" stroke="#BDB7AF" strokeWidth="1" />
                <text x="210" y="115" fontSize="10" fill="#78736C">{t("report.mapRoadLabel")}</text>
              </svg>
              {pinDropped ? (
                <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-full flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center shadow-lg">
                    <Lightbulb size={16} className="text-white" />
                  </div>
                  <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-t-[8px] border-transparent border-t-brand-primary" />
                </div>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="bg-white/90 rounded-lg px-4 py-2 text-sm text-neutral-700 shadow-sm">
                    {t("report.tapToDropPin")}
                  </p>
                </div>
              )}
            </div>
            {pinDropped && (
              <p className="text-xs text-semantic-success-text mt-1 flex items-center gap-1">
                <CheckCircle2 size={12} aria-hidden /> {t("report.pinPlaced")}
              </p>
            )}
          </div>
        ) : (
          <div>
            <label className="block">
              <span className="text-sm font-medium text-neutral-700">{t("report.poleNumber")}</span>
              <p className="text-xs text-neutral-500 mt-0.5 mb-1">
                {t("report.poleNumberHint")}
              </p>
              <input
                type="text"
                value={poleNumber}
                onChange={(e) => setPoleNumber(e.target.value)}
                placeholder={t("report.poleNumberPlaceholder")}
                className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm text-neutral-900 font-mono focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none"
              />
            </label>
          </div>
        )}

        <label className="block">
          <span className="text-sm font-medium text-neutral-700">{t("report.descriptionLabel")}</span>
          <textarea
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("report.descriptionPlaceholder")}
            className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-500 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none"
          />
        </label>

        {photoUploaded ? (
          <div className="rounded-lg border border-semantic-success/30 bg-semantic-success/5 p-3 flex items-center gap-3">
            <CheckCircle2 size={20} className="text-semantic-success-text" aria-hidden />
            <div>
              <p className="text-sm font-medium text-neutral-900">{t("report.photoFilename")}</p>
              <p className="text-xs text-neutral-500">{t("report.photoUploaded")}</p>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setPhotoUploaded(true)}
            className="w-full rounded-lg border-2 border-dashed border-neutral-300 p-3 text-center hover:border-brand-primary hover:bg-brand-primary/5 transition-colors duration-fast"
          >
            <Upload size={16} className="text-neutral-500 mx-auto mb-1" aria-hidden />
            <p className="text-xs text-neutral-500">{t("report.attachPhoto")}</p>
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={method === "map" ? !pinDropped : !poleNumber}
        className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {t("report.submitReport")}
      </button>
    </div>
  );
}
